import { createSelector } from 'reselect';
import isEqual from 'lodash.isequal';
import groupBy from 'lodash.groupby';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  accountTypeKeys,
  accountTypes,
  IRATrusteesType,
  USER_ROLE_TYPES,
} from '../../util/enum/api/accountTypes';
import { formatAddressString } from '../../util/helpers/addressHelper';
import { isAccountVerified } from '../../constants/nagDashboardContants';
import { IRA_EQUITY_TYPE } from '../../util/constants';
import { selectAuthUser } from './userSelectors';

const accountSelector = (state) => state.account.current;
const userPermissionsSelector = (state) => state.account.userPermissions;
const userSettingsSelector = (state) => state.account.settings;
const accountAgreementSelector = (state) => state.accountAgreement;
const nagDashboardSelector = (state) => state.account.current.nagDashboard;

export const getCurrentAccount = createSelector(
  accountSelector,
  (state) => state.current,
);

export const getCurrentAccountUid = createSelector(
  getCurrentAccount,
  (state) => state.AccountUid,
);

export const getAccountAgreement = createSelector(
  accountAgreementSelector,
  (state) => (state ? state.accountAgreement : null),
);

export const selectAccountAgreementHtml = createSelector(
  accountSelector,
  (state) => state?.accountAgreementHtml?.data,
);

export const getCurrentAccountCode = createSelector(
  getCurrentAccount,
  (state) => state.AccountCode,
);

export const getCurrentAccountType = createSelector(
  getCurrentAccount,
  (state) => state.type,
);

export const getCurrentAccountCreatedDate = createSelector(
  getCurrentAccount,
  (state) => state.AddedOn,
);

export const getCurrentAccountNavigationInfo = createSelector(
  getCurrentAccountUid,
  getCurrentAccountType,
  (accountUid, accountType) => ({ accountUid, accountType }),
);

export const selectAccountUsers = createSelector(
  accountSelector,
  (state) => state.accountUsers,
);

export const selectAccountUsersData = createSelector(
  selectAccountUsers,
  (state) => state.data,
);

export const getCurrentUserAccountRow = createSelector(
  selectAccountUsersData,
  selectAuthUser,
  (accountUsers, authUser) =>
    accountUsers.find((u) => u.UserUid === authUser?.UserUid),
);

export const getCurrentUserRoleType = createSelector(
  getCurrentUserAccountRow,
  (row) => row?.RoleType,
);

export const getIsNotificationOnlyUser = createSelector(
  getCurrentUserRoleType,
  (roleType) => roleType === USER_ROLE_TYPES.NOTIFICATION_ONLY,
);

export const getIsReadOnlyUser = createSelector(
  getCurrentUserRoleType,
  (roleType) => roleType === USER_ROLE_TYPES.READ_ONLY,
);

export const getIsFullAccessUser = createSelector(
  getCurrentUserRoleType,
  (roleType) => roleType === USER_ROLE_TYPES.FULL_ACCESS,
);

// The primary account holder (account creator). Not a RoleType — carried as a
// flag on the current user's account-user row.
export const getIsPrimaryUser = createSelector(
  getCurrentUserAccountRow,
  (row) => !!row?.IsPrimaryUser,
);

export const selectAccountUsersError = createSelector(
  selectAccountUsers,
  (state) => state.errorMessage,
);

export const selectAccountAddresses = createSelector(
  accountSelector,
  (state) => state.accountAddresses.data,
);

export const selectMappedAccountAddressesForSelectField = createSelector(
  selectAccountAddresses,
  (state) =>
    state.map((address) => {
      const { AddressUid } = address;

      const addressLabel = formatAddressString(address);

      return {
        label: addressLabel,
        value: AddressUid,
      };
    }),
);
export const selectMappedAccountVerifiedAddressesForSelectField = createSelector(
  selectAccountAddresses,
  (state) =>
    state
      .filter((address) => address.IsVerified)
      .map((address) => {
        const { AddressUid } = address;

        const addressLabel = formatAddressString(address);

        return {
          label: addressLabel,
          value: AddressUid,
        };
      }),
);
export const selectDefaultMailingAddress = createSelector(
  selectAccountAddresses,
  (state) => state.find((address) => address.IsDefaultMailingAddress),
);

export const selectDefaultMailingAddressForSelectField = createSelector(
  selectDefaultMailingAddress,
  (state) => {
    if (!state) {
      return null;
    }
    const {
      AddressUid,
      RecipientCaption,
      AddressLine,
      StateRegion,
      PostalCode,
    } = state;
    const addressLabel = [
      RecipientCaption,
      AddressLine,
      StateRegion,
      PostalCode,
    ]
      .filter(Boolean)
      .join(', ');

    return {
      label: addressLabel,
      value: AddressUid,
    };
  },
);

export const selectAccountAddressesError = createSelector(
  accountSelector,
  (state) => state.accountAddresses.errorMessage,
);

export const selectAddAccountAddresses = createSelector(
  accountSelector,
  (state) => state.newAddress.data,
);

export const selectAddAccountAddressesError = createSelector(
  accountSelector,
  (state) => state.newAddress.error,
);

export const getCurrentUserPermissions = createSelector(
  userPermissionsSelector,
  (state) => state.permissions,
);

export const getCurrentUserPermissionsError = createSelector(
  userPermissionsSelector,
  (state) => state.permissionsError,
);

export const selectWireDepositInstructions = createSelector(
  userSettingsSelector,
  (state) => state.WireDepositInstructions,
);

export const selectCheckDepositInstructions = createSelector(
  userSettingsSelector,
  (state) => state.CheckDepositInstructions,
);

export const selectAccountRestrictionFlags = createSelector(
  userSettingsSelector,
  (state) => state.AccountRestrictionFlags,
);

export const selectIsCreditCardActive = createSelector(
  userSettingsSelector,
  (state) => state.IsCreditCardActive,
);

export const selectOneTimeCreditCardPayment = createSelector(
  userSettingsSelector,
  (state) => state.OneTimeCreditCardPayment,
);

export const selectAgreementTypesToReSign = createSelector(
  userSettingsSelector,
  (state) => state.AgreementTypesToResign,
);

export const selectAccountsWithoutCurrent = createSelector(
  accountSelector,
  (state) =>
    state.allAccounts.data.filter(
      (account) => !isEqual(account, state.current),
    ),
);

export const selectAccounts = createSelector(
  accountSelector,
  (state) => state.allAccounts.data,
);

export const selectGroupedAccounts = createSelector(
  selectAccountsWithoutCurrent,
  (state) =>
    groupBy(state, (account) =>
      parseEnumType(accountTypeKeys, account.AccountType),
    ),
);

export const selectNewUser = createSelector(
  accountSelector,
  (state) => state.accountUsers.data,
);

export const selectNewUserError = createSelector(
  accountSelector,
  (state) => state.accountUsers.error,
);

export const getUpdateAccountError = createSelector(
  accountSelector,
  (state) => state.errorMessage,
);

export const selectNagDashboard = createSelector(
  nagDashboardSelector,
  (state) => state?.data,
);

// Account-level price-alert / Buy-at-Target-Price eligibility (independent of
// feature flags). Ineligible: unverified (KYC), IRA, delivery-only/basic, and
// notification-only users.
export const selectIsPriceAlertEligibleAccount = createSelector(
  getCurrentAccount,
  selectNagDashboard,
  getCurrentUserRoleType,
  (account, nagDashboard, roleType) => {
    if (!account || !account.AccountUid) return false;
    const accountType = parseEnumType(accountTypeKeys, account.AccountType);
    return (
      isAccountVerified(nagDashboard) &&
      accountType !== accountTypes.IRA &&
      accountType !== accountTypes.Basic &&
      roleType !== USER_ROLE_TYPES.NOTIFICATION_ONLY
    );
  },
);

// Equity IRA accounts hold their cash at Equity, so they cannot place Trigger
// Orders. They still SEE the "Buy at Target Price" entry point and are blocked
// with a popup at the point of placing the order.
export const selectIsEquityIRAAccount = createSelector(
  getCurrentAccount,
  (account) => {
    if (!account || !account.AccountUid) return false;
    const accountType = parseEnumType(accountTypeKeys, account.AccountType);
    return (
      accountType === accountTypes.IRA &&
      parseEnumType(IRATrusteesType, account.IRATrusteesType) ===
        IRA_EQUITY_TYPE
    );
  },
);
